import { useState, useCallback } from 'react'
import { useFetchData } from 'utils/hooks/useFetchData'
import * as Types from 'apps/fleet-planner/types'
import {
  fetchShipment,
  updateStatus,
  updateCheckInOut,
  uploadPod,
} from 'apps/fleet-planner/utils/api'

interface IUseDriverViewApiState {
  shipmentDetailsErrors?: string
  shipmentUpdateErrors?: string
  isShipmentDetailsLoading: boolean
  isShipmentUpdating: boolean
  getShipment: (id: string, token: string) => Promise<void>
  uploadPodAttachment: (
    shipmentId: string,
    shipment: Types.IShipmentPodUpdate,
    token: string
  ) => Promise<Types.IGetShipmentResponse | undefined>
  updateShipmentStatus: (
    shipmentId: string,
    shipment: Types.IShipmentStatusUpdate,
    token: string
  ) => Promise<Types.IGetShipmentResponse | undefined>
  updateCheckInOutTimes: (
    shipmentId: string,
    stopId: string,
    stop: Types.IShipmentInOutUpdate,
    token: string
  ) => Promise<{} | undefined>
  load?: Types.ILoad
  isTimeZoneError?: boolean
}

export const INVALID_STATUS_ERROR = 'Error: Invalid status.'
export const INVALID_STATUS_MESSAGE = 'This load is out of date.  Please refresh the page.'
export const FUTURE_DATE_STATUS_ERROR = 'Error: check-in time can not be in the future'
export const FUTURE_DATE_STATUS_MESSAGE =
  "Check-in time is in the future. Ensure your time is based on the facility location's timezone."
export const FUTURE_DATE_INPUT_MESSAGE = 'Use timezone of pickup location'

export const useDriverViewApi = (): IUseDriverViewApiState => {
  const {
    errors: shipmentDetailsErrors,
    fetchData,
    isLoading: isShipmentDetailsLoading,
  } = useFetchData()
  const [load, setLoad] = useState<Types.ILoad>()
  const [isShipmentUpdating, setIsShipmentUpdating] = useState<boolean>(false)
  const [shipmentUpdateErrors, setShipmentUpdateErrors] = useState<string | undefined>('')
  const [isTimeZoneError, setIsTimeZoneError] = useState<boolean>(false)

  /**
   * Fetch shipment details
   */
  const getShipment = useCallback(async (id: string, token: string) => {
    const { shipment } = await fetchData(fetchShipment(id, token))
    setLoad(shipment)
  }, [])

  /**
   * Update shipment status
   */
  const updateShipmentStatus = useCallback(
    async (shipmentId: string, shipment: Types.IShipmentStatusUpdate, token: string) => {
      setIsShipmentUpdating(true)
      setIsTimeZoneError(false)
      try {
        const resp = await updateStatus(shipmentId, shipment, token)
        setShipmentUpdateErrors('')
        setIsShipmentUpdating(false)
        getShipment(shipmentId, token)
        return resp
      } catch (e) {
        let errorMessage = e
        if (e === INVALID_STATUS_ERROR) {
          errorMessage = INVALID_STATUS_MESSAGE
        } else if (e === FUTURE_DATE_STATUS_ERROR) {
          setIsTimeZoneError(true)
          errorMessage = FUTURE_DATE_STATUS_MESSAGE
        }
        setShipmentUpdateErrors(errorMessage)
        setIsShipmentUpdating(false)
        return undefined
      }
    },
    []
  )

  /**
   * Update checkin/checkout times
   */
  const updateCheckInOutTimes = useCallback(
    async (shipmentId: string, stopId: string, stop: Types.IShipmentInOutUpdate, token: string) => {
      setIsShipmentUpdating(true)
      try {
        const resp = await updateCheckInOut(shipmentId, stopId, stop, token)
        setShipmentUpdateErrors('')
        setIsShipmentUpdating(false)
        getShipment(shipmentId, token)
        return resp
      } catch (e) {
        const errorMessage = e === INVALID_STATUS_ERROR ? INVALID_STATUS_MESSAGE : e
        setShipmentUpdateErrors(errorMessage)
        setIsShipmentUpdating(false)
        return undefined
      }
    },
    []
  )

  /**
   * Upload POD attachment file
   */
  const uploadPodAttachment = useCallback(
    async (shipmentId: string, shipment: Types.IShipmentPodUpdate, token: string) => {
      setIsShipmentUpdating(true)
      try {
        const resp = await uploadPod(shipmentId, shipment, token)
        setShipmentUpdateErrors('')
        setIsShipmentUpdating(false)
        getShipment(shipmentId, token)
        return resp
      } catch (e) {
        setShipmentUpdateErrors(e)
        setIsShipmentUpdating(false)
        return undefined
      }
    },
    []
  )

  return {
    shipmentDetailsErrors,
    shipmentUpdateErrors,
    isShipmentDetailsLoading,
    isShipmentUpdating,
    getShipment,
    updateShipmentStatus,
    load,
    updateCheckInOutTimes,
    uploadPodAttachment,
    isTimeZoneError,
  }
}
